// @ts-nocheck
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Message } from '../../types/chat';
import { processLLMResponse } from '../../utils/citationHelper';
import { renderMarkdownContent } from '../../utils/markdownRenderer';
import CitationCard from './CitationCard';
import FeedbackButtons from './FeedbackButtons';
import ProfessionalCitedMessage from './ProfessionalCitedMessage';
import MessageRenderer from './MessageRenderer';
import '../../styles/citationCard.css';
import '../../styles/messageResponse.css';
import '../markdown/markdown.css';
import DOMPurify from 'dompurify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';

/**
 * Component to display a user query with show more/less functionality for long messages
 */
interface UserQueryDisplayProps {
  query: string;
}

const UserQueryDisplay: React.FC<UserQueryDisplayProps> = ({ query }) => {
  const [expanded, setExpanded] = useState(false);
  const isLong = query.length > 150;
  
  const styles = {
    container: {
      paddingBottom: '0.5rem',
      marginBottom: '0.5rem',
      fontSize: '0.875rem',
      lineHeight: 1.5,
      fontWeight: 400,
      letterSpacing: '0.01em',
      color: '#9ca3af'
    },
    button: {
      background: 'none',
      border: 'none',
      color: '#3068D4',
      cursor: 'pointer',
      fontSize: '0.75rem',
      padding: 0,
      textDecoration: 'underline',
      margin: 0
    }
  };

  return (
    <div style={styles.container}>
      <div>
        {isLong && !expanded ? `${query.slice(0, 150)}...` : query}
        {isLong && (
          <button 
            style={styles.button}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'Show less' : 'Show more'}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * Citation source widget that displays formatted source information
 */
interface SourceWidgetProps {
  source: any;
  index: number;
  getSourceColors: (sourceNumber: number) => { bg: string; border: string; text: string };
}

const SourceWidget: React.FC<SourceWidgetProps> = ({ source, index, getSourceColors }) => {
  const getSourceIcon = (url) => {
    if (!url) return null;
    
    try {
      const domain = new URL(url).hostname;
      
      // Detect common social/web platforms
      if (domain.includes('twitter.com') || domain.includes('x.com')) {
        return '𝕏'; // Unicode 'X' symbol for Twitter
      } else if (domain.includes('linkedin.com')) {
        return 'in';
      } else if (domain.includes('github.com')) {
        return <i className="fab fa-github"></i>;
      } else if (domain.includes('youtube.com') || domain.includes('youtu.be')) {
        return <i className="fab fa-youtube"></i>;
      }
      
      // For other domains, return the favicon if available
      return <img src={`https://www.google.com/s2/favicons?domain=${domain}`} alt="" style={{ width: 16, height: 16 }} />;
    } catch (error) {
      return null;
    }
  };
  
  // Local helper function to clean citation text
  const cleanText = (text) => {
    if (!text) return '';
    
    try {
      // For navigation menus or link lists
      if (text && (text.includes('* [') || text.includes('](https://'))) {
        return "See source for details";
      }
      
      // For HTML content
      if (text && /<[a-z][\s\S]*>/i.test(text)) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(text);
        text = tempDiv.textContent || '';
      }
      
      // Replace markdown links with just their text
      text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      
      // Remove citation markers
      text = text.replace(/\[\d+\]/g, '');
      
      // Remove multiple spaces and clean up
      return text.replace(/\s+/g, ' ').trim();
    } catch (e) {
      console.error("Error cleaning text:", e);
      return text || '';
    }
  };
  
  // Helper function to get the best text preview, using same approach as CitationCard
  const getPreviewText = (src) => {
    if (!src) return 'No source data';
    
    let rawText = src.cited_text || src.sentence || src.text || '';
    
    if (!rawText.trim()) {
      return 'No preview available';
    }
    
    // Use same text processing as CitationCard - strip markdown symbols
    rawText = rawText
      // Remove #, ##, ### etc. at start of lines or after spaces
      .replace(/^[#]+\s*/gm, '')
      .replace(/\s+[#]+\s*/g, ' ')
      // Remove standalone ** or * 
      .replace(/\s*[*]+\s*/g, ' ')
      // Remove any backslashes
      .replace(/\\/g, '')
      // Clean up extra spaces and newlines
      .replace(/\s+/g, ' ')
      .trim();
    
    // Take longer excerpt to ensure 3 lines of content
    rawText = rawText.length > 350 ? rawText.substring(0, 350) + '...' : rawText;
    
    try {
      // Sanitize the text like CitationCard does
      return DOMPurify.sanitize(rawText);
    } catch (error) {
      console.error("Error processing source preview text:", error);
      return rawText;
    }
  };
  
  const sourceNumber = source.number || (index + 1);
  const colorClasses = getSourceColors(sourceNumber);
  const previewText = getPreviewText(source);
  const title = source.title || (source.url ? new URL(source.url).hostname : 'Source');

  // Modified to not truncate, since CSS will handle the 3-line display
  const shortExcerpt = previewText;
  
  return (
    <div className={`source-widget ${colorClasses.border}`}>
      <div className="source-marker-container">
        <div className={`source-marker-badge ${colorClasses.bg}`}>
          {sourceNumber}
        </div>
      </div>
      <div className="source-content">
        <div className="source-header">
          <div className="source-icon-container">
            {getSourceIcon(source.url)}
          </div>
          <div className="source-title">
            {source.url ? (
              <a 
                href={source.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="source-title-link"
              >
                {title}
              </a>
            ) : (
              <span>{title}</span>
            )}
          </div>
        </div>
        
        <div className="source-excerpt">
          {shortExcerpt}
        </div>
        
        <div className="source-footer">
          <div className="source-footer-meta">
            {source.url && new URL(source.url).hostname}
          </div>
          <a 
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="view-source-button"
          >
            View Source
          </a>
        </div>
      </div>
    </div>
  );
};

/**
 * Main component for displaying message responses with tabs for response and sources
 */
interface MessageResponseDisplayProps {
  message: Message; // The bot's message object
  userQuery?: string; // The user message that triggered this response
  onFeedback?: (messageId: string, type: 'thumbs_up' | 'thumbs_down' | 'need_review', reason?: string) => void;
}

const LoadingState = ({ userQuery }) => {
  return (
    <div className="message-response-display">
      <div className="loading-content-wrapper">
        {/* Show user query */}
        {userQuery && (
          <div className="user-query-display">
            {userQuery}
          </div>
        )}

        {/* Professional loading with icon and text */}
        <div className="professional-loading">
          <div className="loading-icon">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" className="animate-spin">
              <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="m12 1 0 6m0 10 0 6m11-7-6 0m-10 0-6 0m8.5-8.5 4.24-4.24m-12.02 12.02 4.24-4.24m8.48 0 4.24 4.24m-12.02-12.02 4.24 4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
          <span className="loading-text">Searching for answers...</span>
        </div>

      </div>
    </div>
  );
};

const MessageResponseDisplay: React.FC<MessageResponseDisplayProps> = ({
  message,
  userQuery,
  onFeedback,
  ...props
}) => {
  const [activeTab, setActiveTab] = useState('response');
  // const [formattedContent, setFormattedContent] = useState(''); // Not needed with ProfessionalMarkdownRenderer
  const [showCitationTooltip, setShowCitationTooltip] = useState(null);
  const [citationSources, setCitationSources] = useState([]);
  const containerRef = useRef(null);
  const tabsRef = useRef(null);
  const contentRef = useRef('');
  const citationSourcesRef = useRef([]);
  const stableCitationSources = useRef([]);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);
  const hideTooltipTimerRef = useRef<number | null>(null);
  const [isTabsSticky, setIsTabsSticky] = useState(false);
  
  const stillWaiting = message.isLoading && !message.content;

  // MOVED handleCitationClick definition earlier, before useEffect hooks that use it.
  const handleCitationClick = useCallback((index) => {
    setActiveTab('sources');
    setShowCitationTooltip(null); 
    setTimeout(() => {
      const sourceElement = document.querySelector(`[data-source-index="${index}"]`);
      if (sourceElement) {
        sourceElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        sourceElement.classList.add('highlight-source');
        setTimeout(() => {
          sourceElement.classList.remove('highlight-source');
        }, 2000);
      }
    }, 100);
  }, [setActiveTab, setShowCitationTooltip]);
  
  // Helper function to clean citation text in the main component
  const cleanText = (text, sourceIndex) => {
    if (!text) return '';
    
    try {
      console.log(`Cleaning text for source ${sourceIndex}:`, text.substring(0, 100));
      
      if (text && (text.includes('* [') || text.includes('](https://'))) {
        const completeEvent = message.events?.find(evt => evt.type === 'complete');
        if (completeEvent?.content) {
          const completeContent = completeEvent.content;
          const paragraphRegex = new RegExp(`[^\\n]+\\[${sourceIndex + 1}\\][^\\n]*`, 'g');
          const matchingParagraphs = completeContent.match(paragraphRegex);
          
          if (matchingParagraphs && matchingParagraphs.length > 0) {
            return matchingParagraphs
              .join('\n\n')
              .replace(/\[\d+\]/g, '')
              .trim();
          }
        }
        
        const linkTitles = text.match(/\* \[([^\]]+)\]/g);
        if (linkTitles && linkTitles.length > 0) {
          return linkTitles
            .map(link => link.replace(/\* \[|\]/g, ''))
            .join(', ');
        }
      }
      
      if (text && /<[a-z][\s\S]*>/i.test(text)) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(text);
        text = tempDiv.textContent || '';
      }
      
      text = text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
      text = text.replace(/\[\d+\]/g, '');
      return text.replace(/\s+/g, ' ').trim();
    } catch (e) {
      console.error("Error cleaning text:", e);
      return text || '';
    }
  };
  
  // Stable citation sources processing with content-based filtering
  useEffect(() => {
    console.log('[Citation Sources] Processing sources for message:', message.id);
    
    let sources = [];
    if (message.citation_sources) {
      sources = message.citation_sources;
    } else if (message.metadata?.citation_sources) {
      sources = message.metadata.citation_sources;
    }
    if (sources && !Array.isArray(sources) && typeof sources === 'object') {
      sources = Object.values(sources);
    }
    sources = Array.isArray(sources) ? sources : [];

    console.log('[Citation Sources] All sources from backend:', sources.length, sources);
    
    // Extract citation markers from content to filter sources
    // Support both [Source N] format (new) and [N] format (legacy)
    const messageContent = message.content || '';
    const sourceFormatMatches = messageContent.match(/\[Source\s*(\d+)\]/gi) || [];
    const legacyFormatMatches = messageContent.match(/\[(\d+)\]/g) || [];

    // Extract numbers from both formats
    const sourceFormatNumbers = sourceFormatMatches.map(match => {
      const numMatch = match.match(/\d+/);
      return numMatch ? parseInt(numMatch[0], 10) : 0;
    });
    const legacyFormatNumbers = legacyFormatMatches.map(match => parseInt(match.replace(/[\[\]]/g, ''), 10));

    // Combine and deduplicate
    const referencedNumbers = [...sourceFormatNumbers, ...legacyFormatNumbers];
    const uniqueReferencedNumbers = [...new Set(referencedNumbers)].sort((a, b) => a - b);
    
    console.log('[Citation Sources] Citation markers in content:', [...sourceFormatMatches, ...legacyFormatMatches]);
    console.log('[Citation Sources] Referenced source numbers:', uniqueReferencedNumbers);
    
    // Filter sources to only include those actually referenced in content
    const filteredSources = sources.filter(source => {
      const sourceNumber = source.number || source.marker || source.original_number;
      return uniqueReferencedNumbers.includes(sourceNumber);
    });
    
    console.log('[Citation Sources] Filtered sources (only referenced):', filteredSources.length, filteredSources);
    
    // Create a properly indexed citations array where index matches citation number - 1
    const indexedCitations = [];
    uniqueReferencedNumbers.forEach(num => {
      const source = sources.find(s => (s.number || s.marker || s.original_number) === num);
      if (source) {
        // Place the citation at the correct index (number - 1)
        indexedCitations[num - 1] = {
          title: source.title,
          url: source.url,
          text: source.text,
          cited_text: source.cited_text,
          sentence: source.sentence
        };
      }
    });
    
    // Only update if sources actually changed
    if (JSON.stringify(filteredSources) !== JSON.stringify(stableCitationSources.current)) {
      console.log('[Citation Sources] Updating sources from', stableCitationSources.current.length, 'to', filteredSources.length);
      stableCitationSources.current = filteredSources;
      setCitationSources(filteredSources);
      citationSourcesRef.current = filteredSources;
      
      // Ensure message has citations in metadata for useCitationTooltip hook with proper indexing
      if (!message.metadata) {
        message.metadata = {};
      }
      message.metadata.citations = indexedCitations;
    }
  }, [message.id, message.citation_sources, message.metadata?.citation_sources, message.content]);
  
  // Commented out old processing logic - now using ProfessionalMarkdownRenderer
  /*
  useEffect(() => {
    const isCachedMessage = !message.isStreaming && message.content && message.content.length > 0;
    if (isCachedMessage) {
      try {
        const processed = renderMarkdownContent(message.content, {
          citationSources: citationSourcesRef.current,
          onCitationClick: handleCitationClick
        });
        setFormattedContent(processed || '');
      } catch (error) {
        console.error("Error processing cached message with new renderer:", error);
        // Fallback to old renderer for cached messages
        const processed = processLLMResponse(message.content, {
          citationSources: citationSourcesRef.current,
          onCitationClick: handleCitationClick,
          applySyntaxHighlight: true
        });
        setFormattedContent(processed || '');
      }
      
      setTimeout(() => {
        const markers = document.querySelectorAll('.citation-marker');
        markers.forEach(marker => {
          if (!marker.getAttribute('onmouseover')) {
            const index = parseInt(marker.dataset.citationIndex, 10);
            const sourceNumber = parseInt(marker.dataset.sourceNumber, 10);
            if (!isNaN(index) && !isNaN(sourceNumber)) {
              marker.addEventListener('mouseover', (e) => {
                const rect = marker.getBoundingClientRect();
                const position = { top: rect.top, left: rect.left + (rect.width / 2) };
                document.dispatchEvent(new CustomEvent('tooltip-toggle', { detail: { index, sourceNumber, position } }));
              });
              marker.addEventListener('mouseout', (e) => {
                if (!e.relatedTarget || !e.relatedTarget.closest('.citation-tooltip')) {
                  document.dispatchEvent(new CustomEvent('marker-mouseout', {}));
                }
              });
            }
          }
        });
      }, 100);
    }
  }, [message.id, message.isStreaming, message.content, handleCitationClick]); 
  */
  
  // Commented out old content processing - now using ProfessionalMarkdownRenderer
  /*
  // Content processing - separate from source processing to avoid race conditions
  useEffect(() => {
    const messageContent = message.content || '';
    
    // Only process if content or sources changed
    if (contentRef.current === messageContent && citationSourcesRef.current === citationSources) {
      return;
    }

    console.log('[Content Processing] Processing content with', citationSources.length, 'sources');
    contentRef.current = messageContent;
    
    try {
      if (!messageContent.trim()) {
        setFormattedContent('');
        return;
      }
      
      console.log('[Content Processing] Processing message content:', messageContent.substring(0, 300));
      
      // Use the new professional markdown renderer
      let processed = renderMarkdownContent(messageContent, {
        citationSources: citationSources,
        onCitationClick: handleCitationClick
      });
      
      setFormattedContent(processed || '');
      console.log('[Content Processing] Content processed successfully with new renderer');
      console.log('[Content Processing] Processed HTML:', processed?.substring(0, 300));
    } catch (error) {
      console.error("Error processing content with new renderer:", error);
      // Fallback to the old renderer
      try {
        let fallbackProcessed = processLLMResponse(messageContent, {
          citationSources: citationSources,
          onCitationClick: handleCitationClick,
          applySyntaxHighlight: true
        });
        setFormattedContent(fallbackProcessed || '');
        console.log('[Content Processing] Used fallback renderer');
      } catch (fallbackError) {
        console.error("Fallback renderer also failed:", fallbackError);
        setFormattedContent(messageContent);
      }
    }
  }, [message.content, citationSources, handleCitationClick]);
  */
  
  // Debug citationSources changes
  useEffect(() => {
    console.log('[Citation Sources State] Updated:', citationSources.length, citationSources);
  }, [citationSources]);
  
  // Get source colors based on index for consistent visual appearance
  const getSourceColors = (sourceNumber) => {
    const colorSets = [
      { bg: 'bg-brand-100', border: 'border-brand-200', text: 'text-brand-700' },
      { bg: 'bg-brand-100', border: 'border-brand-200', text: 'text-brand-800' },
      { bg: 'bg-brand-100', border: 'border-brand-200', text: 'text-brand-800' },
      { bg: 'bg-yellow-100', border: 'border-yellow-200', text: 'text-yellow-800' },
      { bg: 'bg-red-100', border: 'border-red-200', text: 'text-red-800' },
      { bg: 'bg-brand-100', border: 'border-brand-200', text: 'text-brand-800' },
    ];
    
    // Use modulo to cycle through colors for large numbers of sources
    const colorIndex = (sourceNumber - 1) % colorSets.length;
    return colorSets[colorIndex];
  };
  
  // Replace getRelativePos with determineTooltipPosition
  const determineTooltipPosition = (markerElement: HTMLElement) => {
    if (!markerElement) return {};

    const rect = markerElement.getBoundingClientRect();
    const PADDING_Y = 8; // space between marker and tooltip
    const TOOLTIP_WIDTH = 420; // same as CitationCard

    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // decide whether to render above or below based on available space
    const renderAbove = spaceBelow < 160 && spaceAbove > spaceBelow; // 160 ≈ tooltip height + padding

    // horizontal center of marker
    let left = rect.left + rect.width / 2;

    // clamp horizontally so tooltip stays within viewport
    const halfWidth = TOOLTIP_WIDTH / 2;
    if (left - halfWidth < 8) {
      left = halfWidth + 8;
    } else if (left + halfWidth > window.innerWidth - 8) {
      left = window.innerWidth - halfWidth - 8;
    }

    const top = renderAbove ? rect.top - PADDING_Y : rect.bottom + PADDING_Y;

    return {
      left: `${left}px`,
      top: renderAbove ? undefined : `${top}px`,
      bottom: renderAbove ? `${window.innerHeight - rect.top + PADDING_Y}px` : undefined,
      transform: 'translateX(-50%)',
      renderAbove,
    } as React.CSSProperties & { renderAbove: boolean };
  };
  
  // Handle content click for citation markers and tooltips
  const handleContentClick = (event: React.MouseEvent<HTMLDivElement>) => {
    // Check if the click was on a citation marker
    let target = event.target as HTMLElement;
    
    // Find the marker if we clicked on a child of it (like the sup element)
    while (target && !target.classList?.contains('citation-marker') && target !== event.currentTarget) {
      target = target.parentElement as HTMLElement;
    }
    
    // If we found a citation marker
    if (target && target.classList?.contains('citation-marker')) {
      // Get the index from the data-onclick attribute
      const index = parseInt(target.dataset.onClick || target.dataset.citationIndex || '-1', 10);
      
      if (!isNaN(index) && index >= 0 && citationSourcesRef.current[index]) {
        // Get the position of the marker for the tooltip
        let position = determineTooltipPosition(target as HTMLElement);
        if (window.innerWidth <= 768) {
          position = {
            top: position.top,
            left: window.innerWidth / 2
          };
        }
        
        // Show the tooltip
        setTooltipPosition(position);
        setActiveTooltipIndex(index);
        setIsTooltipVisible(true);
        
        // Clear any existing hide timer
        if (hideTooltipTimerRef.current) {
          clearTimeout(hideTooltipTimerRef.current);
          hideTooltipTimerRef.current = null;
        }
      }
    }
  };
  
  // Setup document-wide click handler to close tooltip when clicking outside
  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (showCitationTooltip && !e.target.closest('.citation-tooltip') && !e.target.classList.contains('citation-marker')) {
        setShowCitationTooltip(null);
      }
    };
    
    document.addEventListener('click', handleDocumentClick);
    
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [showCitationTooltip]);
  
  // Simple tooltip event handling
  useEffect(() => {
    const handleTooltipToggle = (e) => {
      const { index, sourceNumber, position } = e.detail;
      
      if (index !== undefined && sourceNumber !== undefined && position) {
        // If position is provided directly, use it as is
        // Otherwise calculate position based on marker element if available
        let finalPosition = position;
        
        if (e.detail.markerElement) {
          const markerElement = e.detail.markerElement;
          const markerRect = markerElement.getBoundingClientRect();
          const isWidgetMode = document.querySelector('.ai-chat-widget') || document.querySelector('#ai-chat-widget-container');
          
          // For small screens or widget mode, position in the center of the viewport
          if (window.innerWidth <= 768 || isWidgetMode) {
            finalPosition = {
              top: markerRect.bottom + window.scrollY,
              left: window.innerWidth / 2 // Center horizontally
            };
          } else {
            // Use standard positioning for non-widget mode
            finalPosition = {
              top: markerRect.bottom + window.scrollY,
              left: markerRect.left + (markerRect.width / 2) + window.scrollX
            };
          }
        }
        
        // Clamp horizontally within container bounds using same logic as getRelativePos
        if (containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect();
          const TOOLTIP_WIDTH = 420;
          const PADDING = 8;
          const half = TOOLTIP_WIDTH / 2;
          let adjustedLeft = finalPosition.left - containerRect.left;
          if (adjustedLeft - half < PADDING) {
            adjustedLeft = half + PADDING;
          } else if (adjustedLeft + half > containerRect.width - PADDING) {
            adjustedLeft = containerRect.width - half - PADDING;
          }
          finalPosition = {
            top: finalPosition.top - containerRect.top,
            left: adjustedLeft
          };
        }
        
        setShowCitationTooltip({
          index,
          source: citationSources[index],
          position: finalPosition
        });
      }
    };
    
    // Add mouseout event to hide tooltip
    const handleMouseOut = (e) => {
      // Only proceed if moving from marker and not to the tooltip
      if (e.target.classList.contains('citation-marker') && 
          !e.relatedTarget?.closest('.citation-tooltip')) {
        
        // Set a longer timeout to give user time to move to the tooltip
        hideTooltipTimerRef.current = setTimeout(() => {
          setShowCitationTooltip(prev => {
            // Only hide if not hovering over tooltip or marker
            if (prev && 
                !document.querySelector('.citation-tooltip:hover') && 
                !document.querySelector('.citation-marker:hover')) {
              return null;
            }
            return prev;
          });
        }, 800);
      }
    };
    
    // Handle direct marker-mouseout events
    const handleMarkerMouseOut = () => {
      // Set timeout to hide, but allow time to move to tooltip
      hideTooltipTimerRef.current = setTimeout(() => {
        setShowCitationTooltip(null);
      }, 200);
    };
    
    document.addEventListener('tooltip-toggle', handleTooltipToggle);
    document.addEventListener('mouseout', handleMouseOut, true);
    document.addEventListener('marker-mouseout', handleMarkerMouseOut);
    
    return () => {
      if (hideTooltipTimerRef.current) clearTimeout(hideTooltipTimerRef.current);
      document.removeEventListener('tooltip-toggle', handleTooltipToggle);
      document.removeEventListener('mouseout', handleMouseOut, true);
      document.removeEventListener('marker-mouseout', handleMarkerMouseOut);
    };
  }, [citationSources]);
  
  // Inject CSS overrides for citation markers
  useEffect(() => {
    // Create a style element
    const styleElement = document.createElement('style');
    
    // Set CSS text with high specificity
    styleElement.textContent = `
      .citation-marker {
        display: inline-flex !important;
        align-items: center !important;
        justify-content: center !important;
        font-size: 0.75em !important; /* Slightly smaller font for the number */
        line-height: 1 !important; /* Ensure number is centered vertically */
        min-width: 1.5em !important; /* Minimum width to maintain shape */
        height: 1.5em !important;    /* Height to match min-width for a circle */
        padding: 0 !important;       /* Remove padding, control size with width/height */
        margin: 0 3px !important;    /* Keep some horizontal margin */
        background-color: #DDEAFF !important; /* brand-100 */
        border: 1px solid #4080F2 !important;   /* brand-500 */
        border-radius: 9999px !important;      /* Fully rounded for a circle/pill */
        color: #2554B0 !important;             /* brand-700 for text */
        font-weight: bold !important;
        cursor: pointer !important;
        transition: all 0.2s ease-in-out !important;
        position: relative !important; /* Keep for z-index or future absolute positioning needs */
        z-index: 10 !important; /* Lowered z-index a bit, was 50, tooltip is 99999 */
        user-select: none !important;
        vertical-align: baseline; /* Align better with surrounding text */
      }
      
      .citation-marker:hover {
        background-color: #BBD4FF !important;
        border-color: #3068D4 !important;
        transform: translateY(-1px) !important;
        box-shadow: 0 2px 4px rgba(64, 128, 242, 0.1) !important;
      }
      
      .citation-marker.active {
        background-color: #88B4FF !important;
        border-color: #2554B0 !important;
      }
      
      .citation-tooltip {
        position: absolute !important;
        z-index: 99999 !important;
        visibility: visible !important;
        opacity: 1 !important;
        background-color: #2d3748 !important;
        color: #e2e8f0 !important;
        padding: 10px 15px !important;
        border-radius: 6px !important;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        border: 1px solid #4a5568 !important;
        transform: translateX(-50%) !important;
        width: 350px !important;
        max-width: 350px !important;
        min-width: 350px !important;
        max-height: 250px !important;
        font-size: 13px !important;
        line-height: 1.5 !important;
        overflow: hidden !important;
      }
      
      /* Remove widget-specific styles since we're using the same approach for both */
      
      .citation-tooltip .citation-title {
        font-weight: bold !important;
        margin-bottom: 5px !important;
      }
      
      .citation-tooltip a {
        color: #63b3ed !important;
        text-decoration: underline !important;
        display: block !important;
        margin-bottom: 8px !important;
        word-break: break-all !important;
      }
      
      .citation-tooltip .citation-text {
        margin-top: 8px !important;
      }
    `;
    
    // Append to document head
    document.head.appendChild(styleElement);
    
    // Clean up on component unmount
    return () => {
      // Only remove if still present to avoid NotFoundError
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, []);
  
  // Listen for citation-click events from dynamically created HTML
  useEffect(() => {
    const handleCitationEvent = (e) => {
      const { index } = e.detail;
      if (typeof index === 'number') {
        // First switch to sources tab (tooltip still visible)
        handleCitationClick(index);
      }
    };
    
    document.addEventListener('citation-click', handleCitationEvent);
    
    return () => {
      document.removeEventListener('citation-click', handleCitationEvent);
    };
  }, [handleCitationClick]);

  // Sticky tabs functionality - disabled for now
  // useEffect(() => {
  //   // Sticky tabs code removed
  // }, []);
  
  // Commented out - ProfessionalMarkdownRenderer handles its own citation events
  /*
  // Add event handlers to citation markers whenever content changes
  useEffect(() => {
    const currentSources = citationSources.length > 0 ? citationSources : stableCitationSources.current;
    
    // Only run when we have both formatted content AND citation sources
    if (message.content && currentSources.length > 0) {
      console.log("[MessageResponseDisplay] Content updated, attaching citation marker handlers", `with ${currentSources.length} sources`);
      // Wait briefly for DOM to update after content injection
      setTimeout(() => {
        // Target all citation markers
        const markers = document.querySelectorAll('[data-citation-index]');
        console.log(`Found ${markers.length} citation markers to enhance`);
        
        markers.forEach(marker => {
          console.log('Found marker:', marker, 'with attributes:', Array.from(marker.attributes).map(attr => `${attr.name}="${attr.value}"`));
          
          // Extract marker index
          const indexAttr = marker.getAttribute('data-citation-index');
          if (!indexAttr) {
            console.log('Marker missing data-citation-index attribute');
            return;
          }
          
          const index = parseInt(indexAttr, 10);
          // Only add handlers if we have a valid source for this index
          if (index >= 0 && index < currentSources.length && currentSources[index]) {
            // Only add handlers once to avoid duplicates
            if (marker.hasAttribute('data-citation-handler-added')) return;
            marker.setAttribute('data-citation-handler-added', 'true');
            const newMarker = marker;
            
            // Apply visual styling
            newMarker.classList.add('citation-marker');
            newMarker.style.cursor = 'pointer';
            newMarker.style.backgroundColor = '#DDEAFF';
            newMarker.style.border = '1px solid #4080F2';
            newMarker.style.borderRadius = '4px';
            newMarker.style.padding = '0px 4px';
            newMarker.style.color = '#2554B0';
            newMarker.style.fontWeight = 'bold';
            
            // Add hover handlers
            newMarker.addEventListener('mouseenter', () => {
              console.log(`Citation marker ${index} hovered!`);
              let position = determineTooltipPosition(newMarker as HTMLElement);
              if (window.innerWidth <= 768) {
                position = {
                  top: position.top,
                  left: window.innerWidth / 2
                };
              }
              
              console.log(`Setting tooltip state: index=${index}, position=`, position);
              setTooltipPosition(position);
              setActiveTooltipIndex(index);
              setIsTooltipVisible(true);
              
              // Clear any existing hide timer
              if (hideTooltipTimerRef.current) {
                clearTimeout(hideTooltipTimerRef.current);
                hideTooltipTimerRef.current = null;
              }
              
              console.log(`Tooltip state updated: visible=true, activeIndex=${index}`);
            });
            
            // Add mouse leave handler
            newMarker.addEventListener('mouseleave', (e) => {
              // Check if mouse is moving to the tooltip
              const relatedTarget = e.relatedTarget;
              const isMovingToTooltip = relatedTarget && relatedTarget.closest('.citation-tooltip-force');
              
              if (!isMovingToTooltip) {
                // Start hide timer with delay
                hideTooltipTimerRef.current = setTimeout(() => {
                  setIsTooltipVisible(false);
                }, 300);
              }
            });
          } else {
            console.log(`Skipping marker at index ${index} - no corresponding source (sources length: ${currentSources.length})`);
          }
        });
      }, 100);
    }
  }, [activeTab, message.content, citationSources, handleCitationClick]);
  */
  
  // Debug state changes
  useEffect(() => {
    console.log(`State change: isTooltipVisible=${isTooltipVisible}, activeTooltipIndex=${activeTooltipIndex}`);
  }, [isTooltipVisible, activeTooltipIndex]);
  
  // Add a window resize handler to update tooltip positioning
  useEffect(() => {
    // Only relevant when tooltip is visible
    if (!isTooltipVisible) return;
    
    const handleResize = () => {
      // If we have an active tooltip, update its position
      if (isTooltipVisible && activeTooltipIndex !== null) {
        // Find the marker element
        const marker = document.querySelector(`[data-citation-index="${activeTooltipIndex}"]`);
        if (marker) {
          const rect = marker.getBoundingClientRect();
          
          // Update position based on screen size
          if (window.innerWidth <= 768) {
            setTooltipPosition({
              top: rect.bottom,
              left: window.innerWidth / 2
            });
          } else {
            const clamped = determineTooltipPosition(marker as HTMLElement);
            setTooltipPosition(clamped);
          }
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isTooltipVisible, activeTooltipIndex]);
  
  // Sticky tabs functionality
  useEffect(() => {
    const handleScroll = () => {
      if (!tabsRef.current || !containerRef.current) return;
      
      const tabsRect = tabsRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();
      
      // Check if tabs should be sticky (reached the top)
      const shouldBeSticky = tabsRect.top <= 0 && containerRect.bottom > 100;
      
      if (shouldBeSticky !== isTabsSticky) {
        setIsTabsSticky(shouldBeSticky);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTabsSticky]);
  
  useEffect(() => {
    // Set up citation click handler
    window.handleCitationClick = (index: number) => {
      setActiveTooltipIndex(index);
    };
    
    // Set up citation hover handler
    window.handleCitationHover = (index: number, isHovering: boolean) => {
      if (isHovering) {
        setActiveTooltipIndex(index);
      } else {
        // Optional: You can choose to clear the active citation on hover end
        // setActiveTooltipIndex(null);
        // Or keep it visible until clicked elsewhere
      }
    };

    return () => {
      window.handleCitationClick = null;
      window.handleCitationHover = null;
    };
  }, []);
  
  const clearHideTooltipTimer = useCallback(() => {
    if (hideTooltipTimerRef.current) {
      clearTimeout(hideTooltipTimerRef.current);
      hideTooltipTimerRef.current = null;
    }
  }, []);

  const startHideTooltipTimer = useCallback((immediate = false) => {
    clearHideTooltipTimer(); // Clear any existing timer first
    if (immediate) {
      setIsTooltipVisible(false);
      setActiveTooltipIndex(null);
    } else {
      hideTooltipTimerRef.current = window.setTimeout(() => {
        setIsTooltipVisible(false);
        setActiveTooltipIndex(null);
      }, 200); // Small delay only for marker-tooltip transition
    }
  }, [setIsTooltipVisible, setActiveTooltipIndex, clearHideTooltipTimer]);

  // Default feedback handler if none provided
  const handleFeedback = onFeedback || ((messageId: string, type: string, reason?: string) => {
    console.log('Feedback received:', { messageId, type, reason });
    // TODO: Implement API call to save feedback
  });
  
  // Handle support_step messages - render the widget instead of tabs
  if (message.type === 'support_step' && message.supportStep) {
    // Import SupportFlowWidget dynamically to avoid circular dependencies
    const SupportFlowWidget = require('../support/SupportFlowWidget').default;
    // This message type should be handled by ChatInterface's SupportFlowWidget
    // If we're here, something went wrong - just return null
    return null;
  }

  return (
    <div ref={containerRef} className="message-response-display">
      {stillWaiting ? (
        <LoadingState userQuery={userQuery} />
      ) : (
        <>
          {/* Show user query at the top */}
          {userQuery && (
            <div className="user-query-display">
              {userQuery}
            </div>
          )}

          {/* Legacy tooltip container */}
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', pointerEvents: 'none', zIndex: 99999 }}>
            {(() => {
              const currentSources = citationSources.length > 0 ? citationSources : stableCitationSources.current;
              
              // Check for legacy tooltip system (activeTooltipIndex)
              const hasValidSourceLegacy = currentSources.length > 0 && 
                                   activeTooltipIndex !== null && 
                                   activeTooltipIndex < currentSources.length && 
                                   currentSources[activeTooltipIndex];
              
              // Removed excessive logging
              
              // Render legacy tooltip
              if (isTooltipVisible && tooltipPosition && hasValidSourceLegacy) {
                console.log(`Rendering legacy CitationCard for index ${activeTooltipIndex} with source:`, currentSources[activeTooltipIndex]);
                return (
                  <CitationCard
                    visible={true}
                    source={currentSources[activeTooltipIndex]}
                    position={tooltipPosition}
                    onMouseEnterCard={clearHideTooltipTimer}
                    onMouseLeaveCard={() => startHideTooltipTimer(true)}
                    onRequestClose={() => {
                      clearHideTooltipTimer();
                      setIsTooltipVisible(false);
                      setActiveTooltipIndex(null);
                    }}
                    renderAbove={tooltipPosition?.renderAbove}
                  />
                );
              }
              
              return null;
            })()}
          </div>
          
          
          {/* Tab navigation */}
          <div ref={tabsRef} className="response-tabs">
            <button 
              className={`tab-button ${activeTab === 'response' ? 'active' : ''}`} 
              onClick={() => setActiveTab('response')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="tab-icon">
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Response
            </button>
            {citationSources.length > 0 && (
              <button 
                className={`tab-button ${activeTab === 'sources' ? 'active' : ''}`}
                onClick={() => setActiveTab('sources')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="tab-icon">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Sources ({citationSources.length})
              </button>
            )}
          </div>
          
          {/* Tab content */}
          <div className="tab-content">
            {/* Response tab */}
            {activeTab === 'response' && (
              <div className="response-content">
                {/* Source widgets scrollable container - Add this section */}
                {citationSources.length > 0 && (
                  <div className="source-widgets-container">
                    <div className="source-widgets-scroll">
                      {citationSources.map((source, index) => (
                        <div 
                          key={`source-widget-${index}`}
                          className="source-widget-preview"
                          onClick={() => handleCitationClick(index)}
                        >
                          <div className={`source-marker ${getSourceColors(source.number || (index + 1)).bg}`}>
                            {source.number || (index + 1)}
                          </div>
                          <div className="source-content-preview">
                            <div className="source-title-preview">
                              {source.title || `Source ${source.number || (index + 1)}`}
                            </div>
                            {source.url && (
                              <a 
                                href={source.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="source-link-preview"
                                onClick={(e) => e.stopPropagation()}
                              >
                                Visit
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {(message.content) && (
                  <div className="formatted-response">
                    <MessageRenderer
                      content={message.content}
                      messageId={message.id}
                      className="dark-theme"
                      citationSources={citationSources}
                      onCitationClick={handleCitationClick}
                    />
                  </div>
                )}
                
                {/* Feedback Buttons - Only show for completed bot responses with conversation_id */}
                {!message.isStreaming && !message.isLoading && message.content && message.metadata?.conversation_id && (
                  <FeedbackButtons
                    messageId={message.id}
                    onFeedback={handleFeedback}
                    disabled={message.isStreaming || message.isLoading}
                    isWidgetMode={!!(document.querySelector('.ai-chat-widget') || document.querySelector('#ai-chat-widget-container'))}
                    isAuthenticated={!!localStorage.getItem('auth_token')}
                  />
                )}
                
              </div>
            )}
            
            {/* Sources tab */}
            {activeTab === 'sources' && (
              <div className="sources-content">
                {citationSources.length === 0 ? (
                  <div className="no-sources">No sources available for this response.</div>
                ) : (
                  <div className="sources-list">
                    {citationSources.map((source, index) => (
                      <div key={`source-${index}`} data-source-index={index}>
                        <SourceWidget 
                          source={source} 
                          index={index} 
                          getSourceColors={getSourceColors} 
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MessageResponseDisplay;
